
import React from 'react';
import styles from './_lease-taxes-editor.module.scss';
import {GenericGrid} from "@amsanalytics/react-shared-library";
import { hasValue } from "@amsanalytics/react-shared-library";
import {TileLayout, TileLayoutItem, TilePosition} from "@progress/kendo-react-layout";
import {useDialog} from "../../../../providers/DialogProvider";
import GenericItemEditorDialog, {
  GenericEditorActionType,
  IGenericItemEditorError,
  IGenericItemFieldInfo
} from "../../GenericItemEditorDialog/GenericItemEditorDialog";
import produce from "immer";
import {updateEditingApp} from "../../../../store/application/actions";
import {useDispatch} from "react-redux";
import {ApplicationDispatch} from "../../../../store/application/types";
import {useBusinessRulesEngine} from "../../../../Data/BusinessRulesData";
import {IApplication} from "../../../../Interfaces/Application/IApplication";
import {ILeaseLoanClass} from "../../../../Interfaces/Common/ILeaseLoanClass";
import QuoterError from "../../QuoterError/QuoterError";

export interface ILeaseTaxForGenericEditor {
  fieldName: string;
  description: string;
  rate: number;
}

export interface LeaseTaxesEditorProps {
  currentApp: IApplication;
  leaseQuoteFieldIsDisabled: (fieldName: string) => boolean;
  selectedLeaseLoanClass: ILeaseLoanClass | undefined;
  errorMessage?: string;
}

export default function LeaseTaxesEditor(props:  LeaseTaxesEditorProps) {
  const applicationLeaseQuote = props.currentApp.applicationLeaseQuote;
  const [openDialog, closeDialog] = useDialog();
  const dispatch = useDispatch<ApplicationDispatch>();
  const businessRulesEngine = useBusinessRulesEngine();

  const handleUpdateTax = (updatedTax: ILeaseTaxForGenericEditor): void => {
    const updatedApp = produce(props.currentApp!, updatedCurrentApp => {
      updatedCurrentApp.applicationLeaseQuote[updatedTax.fieldName] = updatedTax.rate;
    })
    dispatch(updateEditingApp(updatedApp, businessRulesEngine));
    closeDialog();
  }

  const editableFieldInfos: IGenericItemFieldInfo[] = [
    {name: "description", label: "Description", type: "string", required: true, readonly: true},
    {name: "rate", label: "Rate", type: "number", required: true, readonly: false}
  ];
  
  const validateTax = (tax: ILeaseTaxForGenericEditor, actionType: GenericEditorActionType): IGenericItemEditorError[] => {
    const errors: IGenericItemEditorError[] = [];

    if (hasValue(tax.rate) && tax.rate! < 0) {
      errors.push({message: "The rate can't be less than 0", canBeBypassed: false});
    }

    return errors;
  }
  
  const editTax = (tax: ILeaseTaxForGenericEditor) => {
    const buttonText = "Update";
    
    openDialog({
      children: (
        <GenericItemEditorDialog
          item={tax as ILeaseTaxForGenericEditor}
          objectSpecificValidator={validateTax}
          title={"Tax"}
          editableFieldInfos={editableFieldInfos}
          submitButtonText={buttonText}
          onSubmit={(updatedTax: ILeaseTaxForGenericEditor) => handleUpdateTax(updatedTax)}
          closeDialog={closeDialog}
        />
      )
    })
  }
  
  // Maps the application quote and their rates to their actual dollar amount to display in each row
  const applicationLeaseQuoteTaxWithAmounts: ILeaseTaxForGenericEditor[] = [
    {fieldName: "stateTaxRate", description: "State Tax", rate: applicationLeaseQuote.stateTaxRate},
    {fieldName: "parishCountyTaxRate", description: "County Tax", rate: applicationLeaseQuote.parishCountyTaxRate},
    {fieldName: "cityTaxRate", description: "City Tax", rate: applicationLeaseQuote.cityTaxRate}
  ];
  
  if (props.selectedLeaseLoanClass?.dualTaxes) {
    applicationLeaseQuoteTaxWithAmounts.push(
      {fieldName: "stateTaxRate2", description: "State Tax 2", rate: applicationLeaseQuote.stateTaxRate2},
      {fieldName: "parishCountyTaxRate2", description: "County Tax 2", rate: applicationLeaseQuote.parishCountyTaxRate2},
      {fieldName: "cityTaxRate2", description: "County Tax 2", rate: applicationLeaseQuote.cityTaxRate2}
    )
  }

  const [data, setData] = React.useState<Array<TilePosition>>([
    { col: 1, colSpan: 12, rowSpan: 1, row: 1 }
  ]);

  const tiles: TileLayoutItem[] = [
    {
      defaultPosition: data[0],
      reorderable: false,
      resizable: false,
      header: 'Taxes',
      body: <div className={styles.leaseTaxesEditorContainer}>
        <GenericGrid<ILeaseTaxForGenericEditor>
          style={{display: 'flex', flex: 1}}
          columns={[
            {title: "Description", field: "description"},
            {title: "Rate", field: "rate", format: '{0}'}
          ]}
          itemName={'Tax'}
          items={applicationLeaseQuoteTaxWithAmounts.sort((a, b) =>
            a.description > b.description ? 1 : -1
          ) ?? []}
          disableAdding
          disableEditing={props.leaseQuoteFieldIsDisabled("taxes")}
          disableExcelExporting={true}
          disablePdfExporting={true}
          disableFiltering={true}
          disableGrouping={true}
          onItemSelected={(tax: ILeaseTaxForGenericEditor) => { if (!props.leaseQuoteFieldIsDisabled("taxes")) editTax(tax); }}
          disablePaging={applicationLeaseQuoteTaxWithAmounts.length <= 20}
        />
        <QuoterError message={props.errorMessage}/>
      </div>,
    }
  ];

  return (
    <TileLayout
      columns={12}
      className={styles.quoteEditorTileLayout}
      gap={{rows: 10, columns: 10}}
      rowHeight={"100%"}
      // style={{display: 'flex', flex: 1}}
      style={{width: '100%'}}
      positions={data}
      items={tiles}
    />
  );
}
